import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidateCustomer } from 'src/api/customer'
import { invalidateProjectCache, useCreateProject, useGetProjectMapSVG, useUpdateProject } from 'src/api/project'
import { AppFormProps } from 'src/components/FormCore'
import ProjectForm from 'src/screens/project/ProjectForm'
import { ShowDialogProps } from 'src/show-dialog'
import { invalidateProjectPositionCache, useGetProjectPositions } from 'src/api/projectPosition'
import FormStepper from './components/component/FormStepper'
import { Box, IconButton, Typography, styled } from '@mui/material'
import { CloseFullscreen, OpenInFull } from '@mui/icons-material'
import ProjectMapComponent from './ProjectMapComponent'
import { EmptyStatusCard } from 'src/components/mooring/EmptyStatusCard'

interface ProjectDialogProps extends ShowDialogProps, AppFormProps {
  title: string
  customerId: EntityId
  localityId: EntityId
  withSteps?: boolean
  steps?: [{ label: string; completed: boolean; active: boolean }]
  onStepSuccess?: (localityId: number) => void
  onStepBack?: () => void
  isProjectUpdate?: boolean
  hasPositions?: boolean
}

export const ProjectDialog: React.FC<ProjectDialogProps> = ({
  title,
  customerId,
  localityId,
  initialValues,
  pop,
  withSteps,
  steps,
  onStepSuccess,
  onStepBack,
  isProjectUpdate = false,
}) => {
  const { t: translate } = useTranslation(['projects', 'common', 'common-api-message'])
  const { mutateAsync: createProject } = useCreateProject(customerId, localityId)
  const { mutateAsync: updateProject, isLoading } = useUpdateProject(customerId, localityId)
  const { data: positions } = useGetProjectPositions(customerId, initialValues?.id)

  const onCreate = form => {
    createProject(form, {
      onSuccess: res => {
        invalidateProjectCache.getProjects(customerId as EntityId)
        invalidateCustomer.getCustomersNameWithProjectPlanningAccess()
        invalidateProjectCache.getAuthorizedProjects(form.customerId)
        toast.success(translate('addedSuccessfully', { ns: 'common' }))
        pop()
        onStepSuccess && onStepSuccess(res.data.project)
      },
      onError: () => {
        toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
      },
    })
  }

  const onUpdate = form => {
    if (form.noOfRows === null || form.noOfRows === undefined) form.noOfRows = initialValues.noOfRows
    if (form.noOfCages === null || form.noOfCages === undefined) form.noOfCages = initialValues.noOfCages
    if (form.noOfPens === null || form.noOfPens === undefined) form.noOfPens = initialValues.noOfPens

    updateProject(form, {
      onSuccess: () => {
        invalidateProjectCache.getProjects(form.customerId)
        invalidateProjectCache.getProject(customerId!, localityId, initialValues?.id as EntityId)
        invalidateProjectPositionCache.getProjectPositions(form.customerId, initialValues?.id as EntityId)
        invalidateProjectCache.getAuthorizedProjects(customerId)
        invalidateProjectCache.getProjectFiles(customerId, localityId, initialValues?.id as EntityId)
        toast.success(translate('updatedSuccessfully', { ns: 'common' }))
        isProjectUpdate && onStepSuccess && onStepSuccess(form)
        pop()
      },
      onError: () => {
        toast.error(translate('unExpectedError', { ns: 'common-api-message' }))
      },
    })
  }
  const isEditing = !!initialValues?.id
  const [isMapFullscreen, setIsMapFullscreen] = useState(false)
  const { data: svgData } = useGetProjectMapSVG(
    customerId,
    localityId,
    initialValues?.id as EntityId,
    isEditing && (positions?.length ?? 0) > 0,
  )

  const hasPositions = useMemo(() => positions && positions?.length > 0, [positions])

  return (
    <>
      {withSteps && steps && <FormStepper steps={steps} />}
      <Box display="flex" gap={2} overflow="hidden">
        <Box
          overflow="hidden"
          maxHeight="79vh"
          sx={{
            transition: 'all 0.3s ease-in-out',
            flex: isMapFullscreen ? 0 : 1,
            opacity: isMapFullscreen ? 0 : 1,
            maxWidth: isMapFullscreen ? 0 : '100%',
            minWidth: isMapFullscreen ? 0 : undefined,
            overflow: isMapFullscreen ? 'hidden' : 'auto',
          }}
        >
          <ProjectForm
            customerId={customerId}
            title={title}
            initialValues={initialValues}
            onCancel={pop}
            onSubmit={initialValues.id ? onUpdate : onCreate}
            localityId={localityId}
            onStepBack={onStepBack}
            isProjectUpdate={isProjectUpdate}
            isLoading={isLoading}
            showMapSVG={!(isEditing || isProjectUpdate)}
          />
        </Box>
        {isEditing && !isProjectUpdate && (
          <ProjectMapPanel fullscreen={isMapFullscreen}>
            {hasPositions ? (
              <>
                <IconButton
                  onClick={() => setIsMapFullscreen(prev => !prev)}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    zIndex: 1,
                    border: '1px solid',
                    borderRadius: '4px',
                    borderColor: 'secondaryLight.main',
                    backgroundColor: 'background.paper',
                  }}
                >
                  {isMapFullscreen ? <CloseFullscreen fontSize="small" /> : <OpenInFull fontSize="small" />}
                </IconButton>
                <OutlinedBox mt={4}>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    {translate('project-map', { ns: 'projects' })}
                  </Typography>
                  <ProjectMapComponent
                    customerId={customerId}
                    localityId={localityId}
                    projectId={initialValues.id as EntityId}
                    height={isMapFullscreen ? '60vh' : 250}
                  />
                </OutlinedBox>
                {svgData && (
                  <OutlinedBox mt={isMapFullscreen ? 4 : 0}>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {translate('technical-drawing', { ns: 'projects' })}
                    </Typography>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'secondaryLight.main',
                        borderRadius: '8px',
                        p: 1,
                      }}
                    >
                      <div
                        dangerouslySetInnerHTML={{ __html: svgData }}
                        style={{ width: '100%', borderRadius: '6px', height: isMapFullscreen ? '60vh' : 240 }}
                      />
                    </Box>
                  </OutlinedBox>
                )}
              </>
            ) : (
              <Box height={'100%'} display="flex" justifyContent="center" alignItems="center">
                <EmptyStatusCard
                  imageWidth="300px"
                  content={translate('empty-map-content')}
                  header={translate('empty-map-header')}
                  contentTopMargin="10px"
                />
              </Box>
            )}
          </ProjectMapPanel>
        )}
      </Box>
    </>
  )
}

export default ProjectDialog

const ProjectMapPanel = styled(Box, {
  shouldForwardProp: prop => prop !== 'fullscreen',
})<{ fullscreen: boolean }>(({ theme, fullscreen }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: fullscreen ? 'row' : 'column',
  gap: theme.spacing(2),
  overflow: 'auto',
  maxHeight: '79vh',
  position: 'relative',
  padding: theme.spacing(2),
  border: `2px solid ${theme.palette.secondaryLight.main}`,
  borderRadius: 4,
  transition: 'all 0.3s ease-in-out',
}))

const OutlinedBox = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.inputBorder.main}`,
  borderRadius: '4px',
  flex: 1,
  padding: theme.spacing(2),
}))
